/**
 * Mirror of `socaity_schemas.platform.job`.
 *
 * Named `CatalogJob` at the package root to avoid clashing with the runtime
 * inference `Job` handle exported from `socaity` / `runtime/job`.
 */

import { createFilePayload } from '../files';

export class JobData {
  display_name: string | null;
  input_data: any;
  output_data: any;
  is_temporary: boolean | null;

  constructor(data: Record<string, any> = {}) {
    this.display_name = data.display_name ?? null;
    this.input_data = data.input_data ?? null;
    this.output_data = data.output_data ?? null;
    this.is_temporary = data.is_temporary ?? null;
  }
}

export class JobFile {
  id: number | null;
  url: string | null;
  file_size_bytes: number | null;
  content_type: string | null;
  purpose: string | null;
  is_public: boolean | null;
  is_temporary: boolean | null;
  created_at: string | null;

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.url = data.url ?? null;
    this.file_size_bytes = data.file_size_bytes != null ? Number(data.file_size_bytes) : null;
    this.content_type = data.content_type ?? null;
    this.purpose = data.purpose ?? null;
    this.is_public = data.is_public ?? null;
    this.is_temporary = data.is_temporary ?? null;
    this.created_at = data.created_at ?? null;
  }
}

export class JobBilling {
  cost_amount: number | null;
  cost_currency: string | null;
  execution_time_s: number | null;
  provider_execution_time_s: number | null;
  provider_delay_time_s: number | null;
  requested_at: string | null;
  dispatched_at: string | null;
  upload_started_at: string | null;
  upload_finished_at: string | null;

  constructor(data: Record<string, any> = {}) {
    this.cost_amount = data.cost_amount != null ? Number(data.cost_amount) : null;
    this.cost_currency = data.cost_currency ?? null;
    this.execution_time_s = data.execution_time_s != null ? Number(data.execution_time_s) : null;
    this.provider_execution_time_s = data.provider_execution_time_s != null
      ? Number(data.provider_execution_time_s) : null;
    this.provider_delay_time_s = data.provider_delay_time_s != null
      ? Number(data.provider_delay_time_s) : null;
    this.requested_at = data.requested_at ?? null;
    this.dispatched_at = data.dispatched_at ?? null;
    this.upload_started_at = data.upload_started_at ?? null;
    this.upload_finished_at = data.upload_finished_at ?? null;
  }
}

export class JobSocial {
  is_public: boolean | null;
  n_views: number | null;
  n_downloads: number | null;
  n_likes: number | null;
  n_shares: number | null;
  liked_by_user: boolean | null;
  downloaded_by_user: boolean | null;

  constructor(data: Record<string, any> = {}) {
    this.is_public = data.is_public ?? null;
    this.n_views = data.n_views != null ? Number(data.n_views) : null;
    this.n_downloads = data.n_downloads != null ? Number(data.n_downloads) : null;
    this.n_likes = data.n_likes != null ? Number(data.n_likes) : null;
    this.n_shares = data.n_shares != null ? Number(data.n_shares) : null;
    this.liked_by_user = data.liked_by_user ?? null;
    this.downloaded_by_user = data.downloaded_by_user ?? null;
  }
}

/** Platform job entity (catalog / user-data facade). */
export class CatalogJob {
  id: string | null;
  status: string | null;
  service_id: string | null;
  deployment_id: string | null;
  endpoint: string | null;
  created_by: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  project_ids: string[];
  data: JobData | null;
  files: JobFile[];
  billing: JobBilling | null;
  social: JobSocial | null;

  constructor(data: Record<string, any> = {}) {
    this.id = data.id ?? null;
    this.status = data.status ?? null;
    this.service_id = data.service_id ?? null;
    this.deployment_id = data.deployment_id ?? null;
    this.endpoint = data.endpoint ?? null;
    this.created_by = data.created_by ?? null;
    this.created_at = data.created_at ?? null;
    this.started_at = data.started_at ?? null;
    this.finished_at = data.finished_at ?? null;
    this.project_ids = Array.isArray(data.project_ids) ? data.project_ids.filter(Boolean) : [];
    this.data = data.data ? new JobData(data.data) : null;
    this.files = Array.isArray(data.files) ? data.files.map((f: any) => new JobFile(f)) : [];
    this.billing = data.billing ? new JobBilling(data.billing) : null;
    this.social = data.social ? new JobSocial(data.social) : null;
  }
}

/**
 * Adapt a platform job (flat `CatalogJob` or legacy composite) into the
 * composite shape the frontend Library warehouse/entities still use.
 */
export function createFullJob(data: Record<string, any> = {}) {
  // Flat catalog job: `{ id, status, data, social, billing, files, ... }`
  if (data && !data.job && data.id) {
    const project_ids = Array.isArray(data.project_ids)
      ? data.project_ids.filter(Boolean)
      : [];
    const billing = data.billing || {};
    const social = data.social || null;
    const jobData = data.data || null;
    return {
      job: {
        id: data.id || null,
        created_at: data.created_at || null,
        finished_at: data.finished_at || null,
        started_at: data.started_at || null,
        status: data.status || null,
        service_id: data.service_id || null,
        api_key: null,
        endpoint: data.endpoint || null,
        user_id: data.created_by || null,
        celery_task_id: null,
        actual_cost_amount: billing.cost_amount ?? null,
        actual_cost_currency: billing.cost_currency ?? null,
        execution_time_s: billing.execution_time_s ?? null,
        provider_execution_time_s: billing.provider_execution_time_s ?? null,
        provider_delay_time_s: billing.provider_delay_time_s ?? null,
        requested_at: billing.requested_at ?? null,
        upload_started_at: billing.upload_started_at ?? null,
        upload_finished_at: billing.upload_finished_at ?? null,
        dispatched_at: billing.dispatched_at ?? null,
      },
      job_data: jobData ? {
        job_id: data.id || null,
        display_name: jobData.display_name ?? null,
        input_data: jobData.input_data ?? null,
        output_data: jobData.output_data ?? null,
        is_temporary: jobData.is_temporary !== undefined ? jobData.is_temporary : false,
      } : null,
      job_social: social ? {
        job_id: data.id || null,
        is_public: social.is_public !== undefined ? social.is_public : null,
        n_views: social.n_views || 0,
        n_downloads: social.n_downloads || 0,
        n_likes: social.n_likes || 0,
        n_shares: social.n_shares || 0,
      } : null,
      liked_by_user: social?.liked_by_user ?? false,
      viewed_by_user: false,
      project_ids,
      project_id: project_ids[0] || null,
      created_by: data.created_by || null,
      created_by_display_name: data.created_by_display_name || null,
      files: Array.isArray(data.files) ? data.files.map((f: any) => createFilePayload(f)) : null,
    };
  }

  const project_ids = Array.isArray(data.project_ids)
    ? data.project_ids.filter(Boolean)
    : (data.project_id ? [data.project_id] : []);

  return {
    job: data.job ? {
      id: data.job.id || null,
      created_at: data.job.created_at || null,
      finished_at: data.job.finished_at || null,
      started_at: data.job.started_at || null,
      status: data.job.status || null,
      service_id: data.job.service_id || null,
      api_key: data.job.api_key || null,
      endpoint: data.job.endpoint || null,
      user_id: data.job.user_id || null,
      celery_task_id: data.job.celery_task_id || null,
      actual_cost_amount: data.job.actual_cost_amount || null,
      actual_cost_currency: data.job.actual_cost_currency || null,
      execution_time_s: data.job.execution_time_s || null,
      provider_execution_time_s: data.job.provider_execution_time_s || null,
      provider_delay_time_s: data.job.provider_delay_time_s || null,
      requested_at: data.job.requested_at || null,
      upload_started_at: data.job.upload_started_at || null,
      upload_finished_at: data.job.upload_finished_at || null,
      dispatched_at: data.job.dispatched_at || null,
    } : null,
    job_data: data.job_data ? {
      job_id: data.job_data.job_id || null,
      display_name: data.job_data.display_name || null,
      input_data: data.job_data.input_data || null,
      output_data: data.job_data.output_data || null,
      is_temporary: data.job_data.is_temporary !== undefined ? data.job_data.is_temporary : false,
    } : null,
    job_social: data.job_social ? {
      job_id: data.job_social.job_id || null,
      is_public: data.job_social.is_public !== undefined ? data.job_social.is_public : null,
      n_views: data.job_social.n_views || 0,
      n_downloads: data.job_social.n_downloads || 0,
      n_likes: data.job_social.n_likes || 0,
      n_shares: data.job_social.n_shares || 0,
    } : null,
    liked_by_user: data.liked_by_user !== undefined ? data.liked_by_user : false,
    viewed_by_user: data.viewed_by_user !== undefined ? data.viewed_by_user : false,
    project_ids,
    project_id: data.project_id || project_ids[0] || null,
    created_by: data.created_by || null,
    created_by_display_name: data.created_by_display_name || null,
    files: Array.isArray(data.files) ? data.files.map((f: any) => createFilePayload(f)) : null,
  };
}
